import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { formatCurrency, formatDate } from '@/lib/utils';
import { useRegenerateInvoicePreview, useRegenerateInvoice } from '@/hooks/useInvoices';
import { CashInvoiceField } from '@/components/billing/CashInvoiceField';
import type { Invoice } from '@jobos/shared';

// Confirm step for rebuilding a final invoice from the job's current breakdown.
//
// Why a dialog rather than a one-click action: regenerating overwrites a
// billing record in place. The old → new total is fetched first so the user
// sees what the breakdown edits actually did to the amount before committing —
// that number is the whole reason they're here.
//
// It also carries the cash switch, which is the only way to fix an invoice
// issued on the wrong side of that flag. Flipping it RECODES THE INVOICE
// NUMBER onto the job's matching count (…-C1), because the C is how the client
// tells a cash invoice apart — so the preview shows the new number the same
// way it shows the new total, and says so louder if the invoice already went
// out.
//
// The number it vacates is retired as a voided invoice, which is why the
// dialog says so: a voided row will appear in this job's list, and the user
// should not have to discover that afterwards and wonder what it is.
//
// The same voided row can be asked for WITHOUT a recode, which is what the
// "Keep a voided record" switch does: the rebuilt invoice keeps its number and
// the amount it replaced stays on the job as a voided row under that same
// number. Off by default — most regenerates are corrections to an invoice that
// never left the office, and a voided twin for each of those is noise.

interface RegenerateInvoiceDialogProps {
  // The invoice to regenerate; null closes the dialog. Passing the row (rather
  // than an id) keeps the header meaningful while the preview is still loading.
  invoice: Invoice | null;
  onOpenChange: (open: boolean) => void;
  jobId: string;
}

export function RegenerateInvoiceDialog({
  invoice,
  onOpenChange,
  jobId,
}: RegenerateInvoiceDialogProps) {
  const open = invoice !== null;
  // null = untouched, so the invoice keeps the flag it has. Unlike the creation
  // dialogs this does NOT fall back to the job: the invoice already exists and
  // its own stored flag is the honest starting point.
  const [cashChoice, setCashChoice] = useState<boolean | null>(null);
  const isCash = cashChoice ?? invoice?.isCash ?? false;

  // Off by default: a regenerate is usually a correction to an invoice nobody
  // outside has seen, and a voided twin for every one of those would bury the
  // real ones.
  const [keepVoidedCopy, setKeepVoidedCopy] = useState(false);

  // Fresh dialog, fresh switches — a flip abandoned on one invoice must not be
  // waiting on the next one opened.
  useEffect(() => {
    if (open) {
      setCashChoice(null);
      setKeepVoidedCopy(false);
    }
  }, [open, invoice?.id]);

  // Asked WITH the switch's position so the preview can name the number the
  // invoice would be recoded to before anything is committed.
  const { data: preview, isLoading, error } = useRegenerateInvoicePreview(
    invoice?.id,
    open,
    isCash,
  );
  const regenerate = useRegenerateInvoice(jobId);

  const handleConfirm = async () => {
    if (!invoice) return;
    try {
      await regenerate.mutateAsync({
        invoiceId: invoice.id,
        isCash,
        // Pointless to ask for alongside a recode, which writes that row
        // unconditionally — and the switch isn't shown in that case.
        keepVoidedCopy: keepVoidedCopy && !recoding,
      });
      onOpenChange(false);
    } catch {
      // The mutation surfaces its own error toast; keep the dialog open so the
      // user can retry or cancel rather than being dropped without feedback.
    }
  };

  const recoding = preview?.newInvoiceNumber != null;
  const unchanged =
    preview != null &&
    preview.currentAmount === preview.newAmount &&
    !preview.sourceDocumentChanged &&
    !recoding;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Regenerate {invoice?.invoiceNumber}?</DialogTitle>
          <DialogDescription>
            This rebuilds the invoice from the job's current breakdown. The status and notes
            stay the same — the amount and date are recalculated. The invoice number only
            changes if you switch it between cash and regular.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error.message}</span>
          </div>
        )}

        {preview && (
          <div className="space-y-3">
            <div className="rounded-lg border bg-gray-50 p-4">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-gray-500">Amount</span>
                <span className="text-sm">
                  <span className="text-gray-500 line-through">
                    {formatCurrency(preview.currentAmount)}
                  </span>
                  <span className="mx-2 text-gray-400">→</span>
                  <span className="text-lg font-semibold">
                    {formatCurrency(preview.newAmount)}
                  </span>
                </span>
              </div>
              <div className="mt-2 flex items-baseline justify-between">
                <span className="text-sm text-gray-500">Invoice date</span>
                <span className="text-sm">
                  <span className="text-gray-500 line-through">
                    {formatDate(preview.currentInvoiceDate)}
                  </span>
                  <span className="mx-2 text-gray-400">→</span>
                  <span className="font-medium">{formatDate(preview.newInvoiceDate)}</span>
                </span>
              </div>
              {/* Only when it is actually changing. The number is the invoice's
                  identity, so it gets the same old → new treatment as the money
                  rather than a footnote. */}
              {preview.newInvoiceNumber && (
                <div className="mt-2 flex items-baseline justify-between">
                  <span className="text-sm text-gray-500">Invoice number</span>
                  <span className="text-sm font-mono">
                    <span className="text-gray-500 line-through">{preview.invoiceNumber}</span>
                    <span className="mx-2 font-sans text-gray-400">→</span>
                    <span className="font-semibold">{preview.newInvoiceNumber}</span>
                  </span>
                </div>
              )}
            </div>

            {recoding && (
              <p className="text-sm text-gray-600">
                <span className="font-mono">{preview.invoiceNumber}</span> will be kept as a
                voided invoice on this job, so the number is still accounted for. It is not
                reused.
              </p>
            )}

            {/* Someone outside holds a document with the old number on it.
                Regenerating is still the right move — the amount they were sent
                is being superseded anyway — but it is a resend, not a silent
                correction. */}
            {recoding && preview.alreadySent && (
              <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  This invoice has already been sent as{' '}
                  <span className="font-mono">{preview.invoiceNumber}</span>. Renumbering it
                  means the client is holding a document that no longer matches — send the new
                  one once it is rebuilt. The send history keeps the original as it went out.
                </span>
              </div>
            )}

            {preview.sourceDocumentChanged && (
              <p className="text-sm text-gray-600">
                The job's active breakdown has changed since this invoice was issued, so its
                line items will change too.
              </p>
            )}

            {unchanged && (
              <p className="text-sm text-gray-600">
                Nothing has changed in the breakdown — regenerating will only refresh the
                invoice date.
              </p>
            )}
          </div>
        )}

        {/* Outside the `preview &&` block: the switch is an INPUT, and hiding it
            while the preview loads would make the dialog jump and the user wait
            to reach the control they opened it for. */}
        {invoice && !error && (
          <CashInvoiceField
            id="regenerate-cash"
            checked={isCash}
            onChange={setCashChoice}
            disabled={regenerate.isPending}
          />
        )}

        {/* Not offered while recoding: that path leaves a voided row behind
            whether or not anyone asks, and the paragraph above already says so.
            An opt-in next to copy promising the same thing would read as a
            choice when it isn't one. */}
        {invoice && !error && !recoding && (
          <label
            htmlFor="regenerate-keep-voided"
            className="flex items-center justify-between rounded-md border border-input px-3 py-2.5"
          >
            <div className="min-w-0 pr-3">
              <p className="text-sm font-medium text-gray-900">Keep a voided record</p>
              <p className="text-xs text-gray-500">
                Leaves <span className="font-mono">{invoice.invoiceNumber}</span> on this job
                as a voided invoice for {formatCurrency(invoice.amount)}, so the
                amount being replaced is still on the record.
              </p>
            </div>
            <Switch
              id="regenerate-keep-voided"
              checked={keepVoidedCopy}
              onCheckedChange={setKeepVoidedCopy}
              disabled={regenerate.isPending}
              aria-label="Keep a voided record"
            />
          </label>
        )}


        {/* The other half of the same rule: payments anchor the send date to
            the live row, so the copy records the amount and nothing else. */}
        {keepVoidedCopy && !recoding && preview?.alreadySent && !preview.sentDateMovesToCopy && (
          <p className="text-sm text-gray-600">
            This invoice keeps its sent date, because payments are recorded against it. The
            voided record is a record of the old amount only.
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={regenerate.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={regenerate.isPending || isLoading || !!error}
            className="bg-blue-900 hover:bg-blue-800 text-white"
          >
            {regenerate.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Regenerate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
